<script lang="ts">
	import type { Layout } from '$models/Layout/Layout';
	import type { NonSubscribableProduct } from '$models/NonSubscribableProduct/NonSubscribableProduct';
	import type { Product } from '$models/Product/Product';
	import { browser } from '$app/environment';
	import { page } from '$app/state';
	import QuantityValidationMessage from '$components/Quantity/QuantityValidationMessage.svelte';
	import ArrowRightIcon from '$components/UI/Icons/ArrowRightIcon.svelte';
	import TrashIcon from '$components/UI/Icons/TrashIcon.svelte';
	import WarningIcon from '$components/UI/Icons/WarningIcon.svelte';
	import { intersectionObserver } from '$lib/actions/intersection-observer.svelte';
	import { getSiteContext } from '$lib/context/site';
	import { trackAddToCart } from '$lib/datalayer/datalayer';
	import { getCartState } from '$lib/state/cart.svelte';
	import { getToastStore } from '$lib/stores';
	import { isRegularProduct } from '$lib/type-helpers';
	import {
		calculateDiscountPercentage,
		formatBlockingPeriod,
		formatCurrency,
		formatNumericString,
		formatRequiredOrderCount
	} from '$lib/utils';
	import { getOrderPricesDiscountData } from '$lib/webparking';
	import { onMount } from 'svelte';
	import { scale } from 'svelte/transition';

	const layout = $derived(page.data.page?.layout as Layout | undefined);
	const translations = $derived(layout?.translations ?? null);
	const pages = $derived(layout?.pages ?? null);
	const cartTranslations = $derived(layout?.cart ?? null);
	const forms = $derived(layout?.forms ?? null);

	const cart = getCartState();
	const toasts = getToastStore();

	const site = getSiteContext();

	let prices = $derived(cart.configurator?.prices());
	let freeNonSubscribableProduct = $derived.by(() => {
		const freeProducts = prices?.freeNonSubscribableProducts ?? [];
		if (freeProducts.length === 0) {
			return [];
		}

		const cartNonSubscribableProducts = cart.nonSubscribableProducts;
		const allNonSubscribableProducts: NonSubscribableProduct[] =
			page.data.products?.nonSubscribable ?? [];

		return freeProducts.map((freeProduct) => {
			const matchesFreeProduct = (product: { id: string; name: string }) => {
				return (
					String(product.id) === String(freeProduct.id) ||
					product.name.trim().toLowerCase() === freeProduct.name.trim().toLowerCase()
				);
			};

			const matchingCartProduct = cartNonSubscribableProducts.find(({ product }) => {
				return matchesFreeProduct(product);
			});
			const matchingCatalogProduct = allNonSubscribableProducts.find((product) => {
				return matchesFreeProduct(product);
			});

			const imageFromApi = (freeProduct as { image?: NonSubscribableProduct['image'] }).image;

			return {
				...freeProduct,
				image:
					imageFromApi ??
					matchingCartProduct?.product.image ??
					matchingCatalogProduct?.image ??
					null
			};
		});
	});
	let savingsPercentage: number = $derived(
		prices
			? calculateDiscountPercentage(
					prices.productsRecurringPrice,
					prices.productsOneTimePrice,
					prices.discountAmount
				)
			: 0
	);
	let selectedFrequencyType: 'recurring' | 'onetime' = $derived(
		(cart.configurator?.selectedFrequency() ?? 0) > 0 ? 'recurring' : 'onetime'
	);
	let shouldShowCart: boolean = $derived.by(() => {
		if (cart.isEmpty()) {
			return false;
		}

		const currentPage = page.data.page;

		if (!currentPage) {
			return false;
		}

		// Do not show on checkout page
		if (currentPage.type === 'checkout') {
			return false;
		}

		return true;
	});
	let isAddingCoupon = $state(false);
	let couponInputValue = $state('');
	let couponFormError = $state('');
	let ctaInViewport = $state(false);
	let hasMounted = $state(false);
	let emptyCartToastTimeout: ReturnType<typeof setTimeout> | null = null;

	async function handleCouponSubmit(e: Event) {
		e.preventDefault();

		if (!cart.configurator) {
			return;
		}

		const normalizedCouponCode = couponInputValue.trim();

		if (!normalizedCouponCode) {
			couponFormError = forms?.cart.discountCodeInvalidError ?? '';
			return;
		}

		const order = {
			...cart.configurator.order(),
			couponCode: normalizedCouponCode
		} as Parameters<typeof getOrderPricesDiscountData>[0];

		couponFormError = '';

		await cart.configurator.refreshPrices();
		const discountData = await getOrderPricesDiscountData(order, cart.configurator.prices());

		if (!discountData.isApplicable) {
			couponFormError = discountData.noApplicableDiscountReason.message;
			return;
		}

		cart.setCouponCode(normalizedCouponCode);
		stopAddCoupon();
	}

	function startAddCoupon() {
		couponFormError = '';
		isAddingCoupon = true;
	}

	function stopAddCoupon() {
		couponFormError = '';
		couponInputValue = '';
		isAddingCoupon = false;
	}

	function removeCoupon() {
		couponFormError = '';
		cart.setCouponCode(null);
	}

	function handleDecrementProductQuantity(product: Product | NonSubscribableProduct) {
		if (!cart.configurator) {
			return;
		}

		cart.configurator.decrementProductQuantity(product);
		cart.syncFromConfigurator();
	}

	function handleIncrementProductQuantity(product: Product | NonSubscribableProduct) {
		if (!cart.configurator) {
			return;
		}

		cart.configurator.incrementProductQuantity(product);
		cart.syncFromConfigurator();
	}

	function handleSetProductQuantity(
		product: Product | NonSubscribableProduct,
		newQuantity: number
	) {
		if (!cart.configurator) {
			return;
		}

		let quantityMultiplier = 1;

		if (isRegularProduct(product) && product.quantityMultiplier) {
			quantityMultiplier = product.quantityMultiplier;
		}

		cart.configurator.setProductQuantity(product, newQuantity / quantityMultiplier);
		cart.syncFromConfigurator();
	}

	function handleFrequencyTypeChange(e: Event) {
		if (!cart.configurator) {
			return;
		}

		const frequencyType = (e.target as HTMLInputElement).value as 'recurring' | 'onetime';
		const frequency =
			frequencyType === 'onetime' ? 0 : cart.configurator.selectedRecurringFrequency();

		cart.setCart({
			...cart.configurator.order(),
			frequency
		});
	}

	function handleRecurringFrequencyChange(e: Event) {
		if (!cart.configurator) {
			return;
		}

		const frequency = parseInt((e.target as HTMLSelectElement).value);

		cart.setCart({
			...cart.configurator.order(),
			frequency
		});
	}

	function handleGoToCheckout() {
		const order = cart.configurator?.order();

		if (order) {
			trackAddToCart(order);
		}

		// Remove the coupon if it's invalid and the user continues anyway
		if (cart.configurator?.couponValidationState() === 'invalid') {
			removeCoupon();
		}
	}

	function handleWindowKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape' && cart.isOpen()) {
			cart.close();
		}
	}

	function clearEmptyCartToastTimeout() {
		if (emptyCartToastTimeout) {
			clearTimeout(emptyCartToastTimeout);
			emptyCartToastTimeout = null;
		}
	}

	$effect(() => {
		clearEmptyCartToastTimeout();

		if (browser && cart.isEmpty() && cart.isOpen() && cartTranslations) {
			emptyCartToastTimeout = setTimeout(() => {
				if (cart.isEmpty() && cart.isOpen() && cartTranslations) {
					toasts.trigger({
						type: 'warning',
						message: cartTranslations.emptyTitle
					});
				}
			}, 150);
		}

		return clearEmptyCartToastTimeout;
	});

	$effect(() => {
		if (browser) {
			cart.isOpen()
				? document.body.classList.add('cart-is-open')
				: document.body.classList.remove('cart-is-open');
		}
	});

	onMount(() => {
		hasMounted = true;
	});
</script>

<svelte:window onkeydown={handleWindowKeydown} />

{#if hasMounted && cart.configurator && shouldShowCart && translations && pages && cartTranslations && forms}
	{@const total =
		cart.configurator.productsTotal() +
		cart.configurator.nonSubscribableProductsTotal() -
		cart.configurator.discount()}
	<div class="popup-cart" class:is-active={cart.isOpen()}>
		<div class="popup-cart-container">
			<div class="popup-cart-main">
				<button
					class="popup-cart-toggle"
					type="button"
					aria-expanded={cart.isOpen()}
					onclick={cart.toggle}
				>
					<div class="popup-cart-toggle-label">{forms.cart.basketLabel}</div>
					<div class="popup-cart-toggle-arrow"></div>
					{#if prices && total && cart.configurator.isValid()}
						<div class="popup-cart-toggle-price">
							{formatCurrency(total)}
						</div>
					{/if}
					<div class="popup-cart-toggle-button">
						{cart.isOpen() ? forms.cart.closeLabel : forms.cart.viewLabel}
					</div>
				</button>
				<!-- Validation message -->
				{#if cart.configurator.isValidCategory() && !cart.configurator.isValidQuantity()}
					<div class="popup-cart-message">
						{#key cart.configurator.selectedQuantity()}
							<div class="popup-cart-message-icon" in:scale={{ start: 1.5, duration: 750 }}>
								<svg
									width="18"
									height="18"
									viewBox="0 0 18 18"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M9 0C7.21997 0 5.47991 0.527841 3.99987 1.51677C2.51983 2.50571 1.36628 3.91131 0.685088 5.55585C0.00389956 7.20038 -0.17433 9.00998 0.172937 10.7558C0.520203 12.5016 1.37737 14.1053 2.63604 15.364C3.89471 16.6226 5.49836 17.4798 7.24419 17.8271C8.99002 18.1743 10.7996 17.9961 12.4442 17.3149C14.0887 16.6337 15.4943 15.4802 16.4832 14.0001C17.4722 12.5201 18 10.78 18 9C17.9975 6.61382 17.0485 4.3261 15.3612 2.63882C13.6739 0.95154 11.3862 0.00251984 9 0ZM9 16.6154C7.49382 16.6154 6.02146 16.1687 4.76912 15.332C3.51678 14.4952 2.5407 13.3058 1.96431 11.9143C1.38792 10.5228 1.23711 8.99155 1.53095 7.51431C1.82479 6.03707 2.55008 4.68014 3.61511 3.61511C4.68014 2.55008 6.03708 1.82478 7.51431 1.53094C8.99155 1.2371 10.5228 1.38791 11.9143 1.9643C13.3058 2.54069 14.4952 3.51677 15.332 4.76912C16.1688 6.02146 16.6154 7.49382 16.6154 9C16.6131 11.019 15.81 12.9547 14.3824 14.3824C12.9547 15.81 11.019 16.6131 9 16.6154ZM8.30769 9.69231V4.84615C8.30769 4.66254 8.38063 4.48645 8.51047 4.35662C8.6403 4.22678 8.81639 4.15385 9 4.15385C9.18361 4.15385 9.3597 4.22678 9.48954 4.35662C9.61937 4.48645 9.69231 4.66254 9.69231 4.84615V9.69231C9.69231 9.87592 9.61937 10.052 9.48954 10.1818C9.3597 10.3117 9.18361 10.3846 9 10.3846C8.81639 10.3846 8.6403 10.3117 8.51047 10.1818C8.38063 10.052 8.30769 9.87592 8.30769 9.69231ZM10.0385 12.8077C10.0385 13.0131 9.97756 13.2139 9.86345 13.3846C9.74934 13.5554 9.58716 13.6885 9.3974 13.7671C9.20765 13.8457 8.99885 13.8663 8.79741 13.8262C8.59597 13.7861 8.41093 13.6872 8.2657 13.542C8.12047 13.3968 8.02156 13.2117 7.98149 13.0103C7.94142 12.8088 7.96199 12.6 8.04059 12.4103C8.11919 12.2205 8.25229 12.0583 8.42306 11.9442C8.59384 11.8301 8.79461 11.7692 9 11.7692C9.27542 11.7692 9.53956 11.8786 9.7343 12.0734C9.92905 12.2681 10.0385 12.5323 10.0385 12.8077Z"
										fill="currentColor"
									/>
								</svg>
							</div>
						{/key}
						<div class="popup-cart-message-content">
							<QuantityValidationMessage
								quantities={cart.configurator.quantities()}
								quantity={cart.configurator.selectedQuantity()}
								multiplier={cart.configurator.quantityMultiplier()}
								{...cart.configurator.selectedCategory()?.translations}
							/>
						</div>
					</div>
				{/if}
				<div class="popup-cart-content">
					<div class="popup-cart-inner">
						<!-- In future multi cart setup, this will be repeated -->
						{#if cart.products.length > 0}
							<div class="popup-cart-group">
								<div class="popup-cart-group-heading">
									<div class="popup-cart-group-title">
										{cart.configurator.selectedCategory()?.name}
									</div>
								</div>
								<div class="popup-cart-products">
									{#each cart.products as { product, quantity }}
										{@const displayQuantity = quantity * (product.quantityMultiplier ?? 1)}
										<div class="popup-cart-product">
											<figure class="popup-cart-product-image">
												{#if product.image}
													<img
														src={product.image.sizes.thumbnail ?? product.image.url}
														alt={product.image.alt}
														width={product.image.width}
														height={product.image.height}
														loading="lazy"
														decoding="async"
													/>
												{/if}
											</figure>
											<div class="popup-cart-product-info">
												<div class="popup-cart-product-name">
													{@html product.name}
												</div>
											</div>
											<div class="popup-cart-product-input">
												<button
													class="popup-cart-product-quantity-button popup-cart-product-quantity-button--minus"
													onclick={() => handleDecrementProductQuantity(product)}
												>
													<span class="screenreader">-</span>
												</button>
												<input
													class="popup-cart-product-quantity"
													type="text"
													value={displayQuantity}
													onchange={(
														e: Event & { currentTarget: EventTarget & HTMLInputElement }
													) => handleSetProductQuantity(product, parseInt(e.currentTarget.value))}
												/>
												<button
													class="popup-cart-product-quantity-button popup-cart-product-quantity-button--plus"
													onclick={() => handleIncrementProductQuantity(product)}
												>
													<span class="screenreader">+</span>
												</button>
											</div>
										</div>
									{/each}
								</div>
								<!-- @TODO: Remove quantity exception -->
								{#if !cart.configurator.isBlocked() && !cart.configurator.isProefpakket()}
									<div class="popup-cart-frequency">
										<div
											class="popup-cart-frequency-pane"
											class:is-inactive={!cart.configurator.isValidQuantity()}
										>
											<div class="popup-cart-frequency-choices">
												<div class="popup-cart-frequency-choice">
													<div class="popup-cart-frequency-option">
														<input
															class="popup-cart-frequency-option-input"
															type="radio"
															name="frequency_type"
															id="onetime"
															value="onetime"
															checked={selectedFrequencyType === 'onetime'}
															onchange={handleFrequencyTypeChange}
														/>
														<label class="popup-cart-frequency-option-label" for="onetime">
															{forms.cart.oneTimeOrderLabel}
														</label>
														{#if prices?.productsOneTimePrice != null}
															<div class="popup-cart-frequency-option-price">
																{formatCurrency(prices.productsOneTimePrice)}
															</div>
														{/if}
													</div>
												</div>

												<div
													class="popup-cart-frequency-choice popup-cart-frequency-choice--recurring"
												>
													<div class="popup-cart-frequency-option">
														<input
															class="popup-cart-frequency-option-input"
															type="radio"
															name="frequency_type"
															id="recurring"
															value="recurring"
															checked={selectedFrequencyType === 'recurring'}
															onchange={handleFrequencyTypeChange}
														/>
														<label class="popup-cart-frequency-option-label" for="recurring">
															{forms.cart.subscribeSaveLabel}
														</label>
														{#if savingsPercentage > 0}
															<div class="popup-cart-frequency-option-savings">
																-{savingsPercentage}%
															</div>
														{/if}
														{#if prices?.productsRecurringPrice != null}
															<div class="popup-cart-frequency-option-price">
																{formatCurrency(prices.productsRecurringPrice)}
															</div>
														{/if}
													</div>
													{#if selectedFrequencyType === 'recurring'}
														<div class="popup-cart-frequency-select">
															<div class="popup-cart-frequency-select-label">
																{forms.cart.chooseFrequencyLabel}
															</div>
															<select
																class="popup-cart-frequency-select-select"
																onchange={handleRecurringFrequencyChange}
															>
																{#each cart.configurator
																	.frequencies()
																	.filter((f) => f > 0) as frequency}
																	<option
																		value={frequency}
																		selected={cart.configurator.selectedRecurringFrequency() ===
																			frequency}
																	>
																		{formatNumericString(
																			translations.frequencySingle,
																			translations.frequencyMultiple,
																			frequency,
																			'%quantity%'
																		)}
																	</option>
																{/each}
															</select>
														</div>
													{/if}
												</div>
											</div>
										</div>
									</div>
								{/if}
							</div>
						{/if}

						{#if cart.nonSubscribableProducts.length > 0}
							<div class="popup-cart-group">
								<div class="popup-cart-group-heading">
									<div class="popup-cart-group-title">{forms.cart.otherProductsLabel}</div>
									{#if cart.configurator.nonSubscribableProductsTotal() > 0}
										<div class="popup-cart-group-total">
											{formatCurrency(cart.configurator.nonSubscribableProductsTotal())}
										</div>
									{/if}
								</div>
								<div class="popup-cart-products">
									{#each cart.nonSubscribableProducts as { product, quantity }}
										<div class="popup-cart-product">
											<figure class="popup-cart-product-image">
												{#if product.image}
													<img
														src={product.image.sizes.thumbnail ?? product.image.url}
														alt={product.image.alt}
														width={product.image.width}
														height={product.image.height}
														loading="lazy"
														decoding="async"
													/>
												{/if}
											</figure>
											<div class="popup-cart-product-info">
												<div class="popup-cart-product-name" title={product.name}>
													{@html product.name}
												</div>
												<div class="popup-cart-product-price">
													{formatCurrency(product.price)}
												</div>
											</div>
											<div class="popup-cart-product-input">
												{#if quantity === 1}
													<button
														class="popup-cart-product-quantity-button popup-cart-product-quantity-button--trash"
														onclick={() => handleDecrementProductQuantity(product)}
													>
														<TrashIcon />
													</button>
												{:else}
													<button
														class="popup-cart-product-quantity-button popup-cart-product-quantity-button--minus"
														onclick={() => handleDecrementProductQuantity(product)}
													>
														<span class="screenreader">-</span>
													</button>
												{/if}
												<input
													class="popup-cart-product-quantity"
													type="text"
													value={quantity}
													onchange={(
														e: Event & { currentTarget: EventTarget & HTMLInputElement }
													) => handleSetProductQuantity(product, parseInt(e.currentTarget.value))}
												/>
												<button
													class="popup-cart-product-quantity-button popup-cart-product-quantity-button--plus"
													onclick={() => handleIncrementProductQuantity(product)}
												>
													<span class="screenreader">+</span>
												</button>
											</div>
										</div>
									{/each}
								</div>
							</div>
						{/if}

						{#if freeNonSubscribableProduct.length > 0}
							<div class="popup-cart-group">
								<div class="popup-cart-group-heading">
									<div class="popup-cart-group-title">{forms.cart.freeProductsLabel}</div>
								</div>
								<div class="popup-cart-products">
									{#each freeNonSubscribableProduct as freeProduct}
										<div class="popup-cart-product">
											{#if freeProduct.image}
												<figure class="popup-cart-product-image">
													<img
														src={freeProduct.image.sizes.thumbnail ?? freeProduct.image.url}
														alt={freeProduct.image.alt}
														width={freeProduct.image.width}
														height={freeProduct.image.height}
														loading="lazy"
														decoding="async"
													/>
												</figure>
											{/if}
											<div class="popup-cart-product-info">
												<div class="popup-cart-product-name" title={freeProduct.name}>
													{@html freeProduct.name}
												</div>
												<div class="popup-cart-product-price">
													{forms.cart.worthAbbreviation}
													{formatCurrency(freeProduct.price)}
												</div>
											</div>
											<div class="popup-cart-product-text">
												{freeProduct.quantity}x
											</div>
										</div>
									{/each}
								</div>
							</div>
						{/if}

						{#if cart.configurator.isValid()}
							<div class="popup-cart-summary">
								<div class="popup-cart-summary-item">
									<div class="popup-cart-summary-item-label">
										{#if cart.couponCode}
											{forms.cart.discountCodeLabel}:
											<strong>{cart.couponCode}</strong>
											{#if cart.configurator.couponValidationState() === 'invalid'}
												<span class="popup-cart-summary-item-label-icon">
													<WarningIcon />
												</span>
											{/if}
										{:else}
											{forms.cart.discountCodeLabel}
										{/if}
									</div>
									<div class="popup-cart-summary-item-value popup-cart-summary-item-value--coupon">
										{#if isAddingCoupon}
											<form class="popup-cart-summary-item-form" onsubmit={handleCouponSubmit}>
												<input
													class="popup-cart-summary-item-form-field"
													type="text"
													bind:value={couponInputValue}
													required
												/>
												<button class="popup-cart-summary-item-form-submit" type="submit">
													<ArrowRightIcon />
												</button>
											</form>
											<button
												class="popup-cart-summary-item-link"
												type="button"
												onclick={stopAddCoupon}
											>
												{forms.cart.discountCodeCancelLabel}
											</button>
										{:else if cart.couponCode}
											<button
												class="popup-cart-summary-item-link"
												type="button"
												onclick={removeCoupon}>{forms.cart.discountCodeRemoveLabel}</button
											>
										{:else}
											<button
												class="popup-cart-summary-item-link"
												type="button"
												onclick={startAddCoupon}>{forms.cart.discountCodeAddLabel}</button
											>
										{/if}
										{#if couponFormError}
											<div class="popup-cart-error">
												{couponFormError}
											</div>
										{/if}
									</div>
								</div>
								{#if prices}
									<div class="popup-cart-summary-item">
										<div class="popup-cart-summary-item-label">{forms.cart.amountLabel}</div>
										<div class="popup-cart-summary-item-value">
											{formatCurrency(
												cart.configurator.productsTotal() +
													cart.configurator.nonSubscribableProductsTotal()
											)}
										</div>
									</div>
									{#if cart.configurator.discount() > 0}
										<div class="popup-cart-summary-item">
											<div class="popup-cart-summary-item-label">{forms.cart.discountLabel}</div>
											<div class="popup-cart-summary-item-value">
												-{formatCurrency(cart.configurator.discount())}
											</div>
										</div>
									{/if}
								{/if}
								{#if cart.configurator.isBlocked()}
									{@const blockingPeriod = parseInt(cart.configurator.blockingPeriod() ?? '0')}
									{@const requiredOrderCount = cart.configurator.requiredOrderCount()}
									<div class="popup-cart-summary-item">
										<div class="popup-cart-summary-item-label">
											{forms.cart.deliveryFrequencyLabel}
										</div>
										<div class="popup-cart-summary-item-value">
											{forms.cart.deliveryFrequencyPrefix}
											{formatNumericString(
												translations.frequencySingle,
												translations.frequencyMultiple,
												cart.configurator.selectedFrequency(),
												'%quantity%'
											)}
										</div>
									</div>
									<div class="popup-cart-summary-item">
										<div class="popup-cart-summary-item-label">
											{forms.cart.blockingPeriodLabel}
										</div>
										<div class="popup-cart-summary-item-value">
											{#if requiredOrderCount !== null}
												{formatRequiredOrderCount(requiredOrderCount, site.locale.code)}
											{:else if blockingPeriod}
												{formatBlockingPeriod(blockingPeriod, site.locale.code)}
											{:else}
												-
											{/if}
										</div>
									</div>
								{/if}
							</div>
						{/if}
					</div>
					{#if cart.configurator.isValid()}
						<div class="popup-cart-footer" class:is-fixed={!ctaInViewport}>
							{#if prices}
								<div class="popup-cart-totals">
									<div class="popup-cart-totals-label">
										<strong>{forms.cart.totalLabel}</strong>
										<small>{forms.cart.freeShippingLabel}</small>
									</div>
									<div class="popup-cart-totals-value">
										{formatCurrency(
											cart.configurator.productsTotal() +
												cart.configurator.nonSubscribableProductsTotal() -
												cart.configurator.discount()
										)}
									</div>
								</div>
							{/if}
							{#if cart.couponCode && cart.configurator.couponValidationState() === 'invalid'}
								<div class="popup-cart-error-message">
									<div class="popup-cart-error-message-icon">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="18"
											height="18"
											fill="none"
											viewBox="0 0 18 18"
											><path
												fill="currentColor"
												d="M9 1.688A7.312 7.312 0 1 0 16.313 9 7.32 7.32 0 0 0 9 1.688m0 13.5A6.187 6.187 0 1 1 15.188 9 6.195 6.195 0 0 1 9 15.188m-.562-5.626V5.626a.563.563 0 0 1 1.124 0v3.938a.563.563 0 1 1-1.124 0m1.406 2.532a.844.844 0 1 1-1.687 0 .844.844 0 0 1 1.687 0"
											/></svg
										>
									</div>
									<div class="popup-cart-error-message-content">
										{cart.configurator.couponErrorMessage() || 'Ongeldige kortingscode'}
									</div>
								</div>
							{/if}
							<div class="popup-cart-submit">
								<a
									href={pages.checkoutPage}
									class="button"
									class:disabled={!cart.configurator.isValid()}
									onclick={handleGoToCheckout}
									use:intersectionObserver={{
										callback: (isIntersecting: boolean) => (ctaInViewport = isIntersecting)
									}}
								>
									{forms.cart.checkoutButtonLabel}
								</a>
							</div>
						</div>
					{/if}
				</div>
			</div>
		</div>
		<button class="popup-cart-backdrop" onclick={cart.close}>
			<span class="screenreader">{forms.cart.closeLabel}</span>
		</button>
	</div>
{/if}

<style lang="postcss">
	.popup-cart {
		&-container {
			position: fixed;
			bottom: -1px;
			right: 0;
			z-index: 9998;
			min-width: 100%;

			.is-active & {
				z-index: 9999;
			}

			@media (--xs) {
				bottom: 0;
				min-width: 395px;
				right: var(--spacing-5);
			}
		}

		&-backdrop {
			position: fixed;
			z-index: 998;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			background-color: rgba(0, 0, 0, 0.5);
			opacity: 0;
			visibility: hidden;
			transition:
				opacity 0.3s ease-in-out,
				visibility 0.3s ease-in-out;

			.is-active & {
				opacity: 1;
				visibility: visible;
			}
		}

		&-message {
			display: flex;
			align-items: center;
			padding: var(--spacing-3) var(--spacing-5);
			background-color: var(--color-secondary);

			&-icon {
				display: flex;
				align-items: center;
				margin-right: var(--spacing-2);
			}

			&-content {
				font-size: 13px;
				line-height: 1;
			}
		}

		&-error-message {
			display: flex;
			align-items: center;
			gap: 5px;
			color: var(--color-red);
			font-size: 14px;
			line-height: 1.2;
			padding: 10px;
			background-color: var(--color-red-light);

			&:not(:last-child) {
				margin-bottom: var(--spacing-3);
			}

			&-icon {
				align-self: flex-start;
			}

			&-content {
				flex: 1;
			}
		}

		&-main {
			position: relative;
			z-index: 2;
			display: flex;
			flex-direction: column;
			width: 100%;
			max-width: 100%;
			border-radius: var(--spacing-3) var(--spacing-3) 0 0;
			overflow: hidden;
			max-height: calc(100vh - 100px);
			max-height: calc(100dvh - 18px);

			@media (--xs) {
				max-width: 392px;
			}
		}

		&-toggle {
			display: flex;
			align-items: center;
			width: 100%;
			padding: 14px 16px;
			color: var(--color-white);
			background-color: var(--color-brown);
			transition: background-color var(--transition);

			&:hover {
				cursor: pointer;
				background-color: color-mix(in srgb, var(--color-brown) 90%, black);
			}

			&-label {
				font-family: var(--title-font-family);
				font-weight: var(--title-font-weight);
				text-transform: var(--box-title-text-transform);
				line-height: var(--font-lineheight-0);
				margin-right: 10px;
				margin-left: 4px;
			}

			&-arrow {
				display: block;
				width: 10px;
				height: 10px;
				transform: rotate(225deg);
				margin-top: 6px;
				margin-right: auto;
				border-right: 1.5px solid var(--color-brown);
				border-bottom: 1.5px solid var(--color-brown);

				.is-active & {
					margin-top: -5px;
					transform: rotate(45deg);
				}
			}

			&-button {
				margin-left: var(--spacing-3);
				padding: var(--spacing-2) var(--spacing-3);
				font-family: var(--font-secondary);
				font-weight: normal;
				font-size: 12px;
				line-height: var(--font-lineheight-0);
				text-transform: uppercase;
				color: var(--button-color);
				background-color: var(--button-background);
				border-radius: 15px;
			}

			&-price {
				opacity: 1;
				transition: opacity 0.3s ease;

				.is-active & {
					opacity: 0;
				}
			}
		}
		&-content {
			position: relative;
			background-color: var(--color-white);
			max-height: 0;
			overflow: hidden;
			transition: max-height 0.35s ease;

			.is-active & {
				max-height: 100vh;
				overflow: auto;
			}
		}
		&-inner {
			padding: var(--spacing-3);
		}
		&-group {
			border: 1px solid var(--color-primary);
			border-radius: var(--spacing-2);
			padding: var(--spacing-4);
			overflow: hidden;
			max-width: calc(100vw - 24px);

			&:not(:last-child) {
				margin-bottom: var(--spacing-3);
			}

			&-heading {
				display: flex;
				align-items: center;
				margin-bottom: var(--spacing-3);
			}

			&-title {
				font-family: var(--title-font-family);
				font-weight: var(--title-font-weight);
				text-transform: var(--box-title-text-transform);
				color: var(--title-color);
			}

			&-total {
				margin-left: auto;
			}
		}

		&-products {
			position: relative;

			&-error {
				position: absolute;
				top: calc(100% + 14px);
				right: 0;
				color: var(--color-red);
				font-size: 15px;
				line-height: var(--font-lineheight-0);
			}
		}

		&-product {
			display: flex;
			align-items: center;

			&:not(:last-child) {
				margin-bottom: 10px;
			}

			&-image {
				width: 48px;
				height: 40px;
				margin-right: var(--spacing-2);
				align-self: flex-start;

				img {
					display: block;
					width: 100%;
					height: 100%;
					object-fit: cover;
				}
			}

			&-info {
				padding-right: var(--spacing-3);
				flex: 1;
				font-size: 15px;
				line-height: var(--font-lineheight-0);
				color: var(--title-color);
				overflow: hidden;
			}

			&-name {
				font-family: var(--title-font-family);
				font-weight: var(--title-font-weight);
				text-transform: var(--box-title-text-transform);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;

				&:not(:last-child) {
					margin-bottom: 4px;
				}
			}

			&-price {
				font-size: 15px;
			}

			&-text {
				font-size: 15px;
				align-self: flex-end;
			}

			&-input {
				display: flex;
				align-items: center;
				border: 1px solid var(--border-color);
				padding-left: 4px;
				padding-right: 4px;
				border-radius: 18px;
			}

			&-quantity {
				text-align: center;
				border: 0;
				line-height: 1.2;
				padding: 10px 0;
				max-width: 36px;
				max-height: 34px;

				&-button {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 24px;
					height: 24px;
					color: var(--color-promo);

					&:before,
					&:after {
						content: '';
						position: absolute;
						width: 10px;
						height: 2px;
						border-radius: 1px;
						background: var(--color-promo);
					}

					&:after {
						transform: rotate(90deg);
					}

					&--minus {
						&:after {
							display: none;
						}
					}

					&--trash {
						&:after,
						&:before {
							display: none;
						}
					}
				}
			}
		}
		&-frequency {
			border-radius: var(--radius-2);
			overflow: hidden;

			&-pane {
				max-height: 124px;
				transition: all 0.35s ease;

				&.is-inactive {
					max-height: 0;
					opacity: 0;
					visibility: hidden;
				}
			}

			&-choices {
				padding-top: var(--spacing-1);
			}

			&-choice {
				padding: var(--spacing-3);

				&--recurring {
					background-color: color-mix(in srgb, var(--color-green) 20%, white);
				}
			}

			&-option {
				display: flex;
				align-items: center;
				font-size: 15px;
				line-height: var(--font-lineheight-1);

				&-price {
					margin-left: auto;
				}

				&-input {
					display: none;

					&:checked + label:after {
						opacity: 1;
					}
				}

				&-label {
					position: relative;
					padding-left: 27px;

					&:before,
					&:after {
						content: '';
						position: absolute;
						display: block;
						border-radius: 50%;
					}

					&:before {
						top: 0;
						left: 0;
						width: 20px;
						height: 20px;
						border: 1px solid var(--color-promo);
						background-color: var(--color-white);
					}

					&:after {
						top: 4px;
						left: 4px;
						width: 12px;
						height: 12px;
						background-color: var(--color-promo);
						opacity: 0;
						transition: opacity 0.2s ease-in-out;
					}

					&:hover {
						cursor: pointer;

						&:after {
							opacity: 0.5;
						}
					}
				}

				&-savings {
					margin-left: 5px;
					color: var(--color-brown);
					padding: 3px 5px;
					font-size: 12px;
					line-height: var(--font-lineheight-0);
					border-radius: 4px;
					background-color: var(--color-green);
				}
			}

			&-select {
				display: flex;
				font-size: 14px;
				padding-left: 27px;
				&-label {
					flex: 1;
				}
				&-select {
					width: auto;
					padding: 0;
					background: none;
					border: 0;
					font-size: inherit;
					line-height: 1.2;
					text-align: right;
					background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iNiIgdmlld0JveD0iMCAwIDEwIDYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xMCAwLjg1NzQyMkw1LjY1NDMgNS4xMjAxMkw1LjY3Njc2IDUuMTQyNThMNC44MDM3MSA2TDAgMS4yODYxM0wwLjg3MzA0NyAwLjQyODcxMUw0Ljc4MTI1IDQuMjYzNjdMOS4xMjU5OCAwTDEwIDAuODU3NDIyWiIgZmlsbD0iYmxhY2siLz4KPC9zdmc+Cg==');
					background-position: right calc(50% + 2px);
					background-repeat: no-repeat;
					padding-right: 14px;
				}
			}
		}

		&-summary {
			padding: var(--spacing-3);

			&-item {
				display: flex;
				font-size: 15px;
				line-height: var(--font-lineheight-1);

				&:not(:last-child) {
					margin-bottom: var(--spacing-1);
				}

				&-label {
					display: flex;
					align-items: center;
					gap: 4px;
					flex: 1;
					padding-right: var(--spacing-2);

					&-icon {
						display: inline-block;
						color: var(--color-red);
					}
				}

				&-value {
					margin-left: auto;

					&--coupon {
						max-width: 160px;
					}
				}

				&-link {
					color: var(--color-promo);
					text-decoration: underline;
					transition: color var(--transition);

					&:hover {
						cursor: pointer;
						color: var(--color-primary-dark);
					}
				}

				&-form {
					display: flex;

					&-field {
						flex: 1;
						padding: 6px 8px;
						/* force 16 px font size to prevent zoom on iOS */
						/* See: https://css-tricks.com/16px-or-larger-text-prevents-ios-form-zoom/ */
						font-size: 16px;
						line-height: var(--font-lineheight-2);
						max-height: 34px;
					}

					&-submit {
						display: flex;
						align-items: center;
						justify-content: center;
						width: 34px;
						height: 34px;
						background-color: var(--color-primary);
						margin-left: var(--spacing-2);
						transition: background-color var(--transition);

						&:hover {
							cursor: pointer;
							background-color: color-mix(in srgb, var(--color-primary) 90%, black);
						}
					}
				}
			}
		}

		&-footer {
			position: sticky;
			bottom: 0;
			left: 0;
			padding: var(--spacing-3);
			background-color: var(--color-white);
			box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
		}

		&-totals {
			display: flex;
			margin: 0 var(--spacing-3);
			padding-top: var(--spacing-1);
			padding-bottom: var(--spacing-5);

			&-label {
				flex: 1;
				line-height: var(--font-lineheight-1);

				small {
					display: block;
					font-size: 13px;
				}
			}

			&-value {
				margin-left: auto;
				align-self: flex-end;
				font-size: var(--font-size-3);
				font-family: var(--font-secondary);
				font-weight: normal;
				text-transform: uppercase;
				line-height: var(--font-lineheight-0);
			}
		}
		&-submit {
			display: flex;

			.button {
				width: 100%;
			}
		}
		&-error {
			color: var(--color-red);
			font-size: 14px;
			line-height: var(--font-lineheight-0);
		}
	}
</style>
